// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EditablePhaseDate } from '@/components/editable-phase-date';
import { MultiTimezoneTooltip } from '@/components/multi-timezone-tooltip';
import { Phase, PhaseIndicator } from '@/components/phase-indicator';
import { EditRounded } from '@/constants/hardfin-standard-icons';
import { ShipmentDisplayStatus, ShipmentType } from '@/shipment/api/types';
import {
  ShipmentThirdPartySerialWarningPhase,
  ShipmentThirdPartySerialWarningPhases,
} from '@/shipment/hooks/use-shipment-third-party-serial-warning';
import { IconButton, Stack } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

interface Props {
  currentStatus: ShipmentDisplayStatus;
  shipmentType: ShipmentType;
  cancelledReason?: string | null;
  createdAt: DateTime;
  shippedAt?: DateTime | null;
  deliveredAt?: DateTime | null;
  receivedAt?: DateTime | null;
  thirdPartySerialWarningPhases?: ShipmentThirdPartySerialWarningPhases;
  /**
   * Callback to edit the shipped date. When provided, the shipped date
   * will be editable via inline popover. Superseded by
   * `onEditShipConfirmation` when both are provided.
   */
  onEditShippedDate?: (newDate: DateTime) => Promise<void>;
  /**
   * Callback to open the ship confirmation dialog in edit mode. When
   * provided (outbound shipments), the shipped phase shows an edit icon
   * opening the full dialog (date, note, references).
   */
  onEditShipConfirmation?: () => void;
  /**
   * Callback to open the delivery confirmation dialog in edit mode. When
   * provided, the delivered phase shows an edit icon opening the full
   * dialog (date, note).
   */
  onEditDeliveryConfirmation?: () => void;
  /**
   * Callback to open the receive dialog in edit mode. When provided
   * (inbound shipments), the received phase shows an edit icon opening
   * the receive references editor.
   */
  onEditReceiveConfirmation?: () => void;
}

const OUTBOUND_TERMINAL_STATUSES: ShipmentDisplayStatus[] = [
  'delivered',
  'cancelled',
];
const INBOUND_TERMINAL_STATUSES: ShipmentDisplayStatus[] = [
  'received',
  'cancelled',
];
const THIRD_PARTY_SERIAL_WARNING_COPY_PREFIX =
  'The shipping carrier reports that this package has already been';
const THIRD_PARTY_SERIAL_WARNING_COPY_SUFFIX =
  "but your 3PL hasn't confirmed the serials yet. Hardfin cannot finish processing the shipment until serials are confirmed.";

/**
 * Formats a DateTime for display in the phase indicator subtext.
 */
function formatDateSubtext(date: DateTime): string {
  return date.toFormat('MMM d, yyyy');
}

function getThirdPartySerialWarningTooltip(
  phase: ShipmentThirdPartySerialWarningPhase
): string {
  return `${THIRD_PARTY_SERIAL_WARNING_COPY_PREFIX} ${phase}, ${THIRD_PARTY_SERIAL_WARNING_COPY_SUFFIX}`;
}

function hasThirdPartySerialWarningPhase(
  phases: ShipmentThirdPartySerialWarningPhases | undefined,
  phase: ShipmentThirdPartySerialWarningPhase
): boolean {
  return phases?.includes(phase) ?? false;
}

interface PhaseDateWithDialogEditProps {
  date: DateTime;
  editLabel: string;
  onEdit: () => void;
}

/**
 * Phase subtext that pairs the phase date with an edit icon opening the
 * corresponding confirmation dialog (ship / delivery / receive). Visual
 * treatment mirrors EditablePhaseDate, but the icon delegates to a dialog
 * instead of an inline popover.
 */
function PhaseDateWithDialogEdit(props: PhaseDateWithDialogEditProps) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.25}>
      <MultiTimezoneTooltip timestamp={props.date} placement="bottom">
        {formatDateSubtext(props.date)}
      </MultiTimezoneTooltip>
      <IconButton
        size="small"
        onClick={props.onEdit}
        aria-label={props.editLabel}
        sx={{ padding: 0.25 }}
      >
        <EditRounded sx={{ fontSize: '0.875rem' }} color="action" />
      </IconButton>
    </Stack>
  );
}

/**
 * Component that displays the current phase of a shipment.
 * Shows a stepper-style indicator with different phases based on shipment type.
 * Outbound: Open → Shipped → Delivered
 * Inbound: Open → Shipped → Delivered → Received
 *
 * When dates are available, they are displayed as subtexts with MultiTimezoneTooltip.
 */
export function ShipmentPhaseIndicator(props: Props) {
  const phases = useMemo(() => {
    const basePhases: Phase<ShipmentDisplayStatus>[] = [
      {
        status: 'open',
        label: 'Open',
        number: 1,
        subtext: (
          <MultiTimezoneTooltip timestamp={props.createdAt} placement="bottom">
            {formatDateSubtext(props.createdAt)}
          </MultiTimezoneTooltip>
        ),
      },
      {
        status: 'shipped',
        label: 'Shipped',
        number: 2,
        warningTooltip: hasThirdPartySerialWarningPhase(
          props.thirdPartySerialWarningPhases,
          'shipped'
        )
          ? getThirdPartySerialWarningTooltip('shipped')
          : undefined,
        subtext:
          props.shippedAt && props.onEditShipConfirmation ? (
            <PhaseDateWithDialogEdit
              date={props.shippedAt}
              editLabel="Edit ship confirmation"
              onEdit={props.onEditShipConfirmation}
            />
          ) : props.onEditShippedDate ? (
            <EditablePhaseDate
              date={props.shippedAt ?? null}
              onSave={props.onEditShippedDate}
              maxDate={props.deliveredAt}
              label="Ship date"
            />
          ) : props.shippedAt ? (
            <MultiTimezoneTooltip
              timestamp={props.shippedAt}
              placement="bottom"
            >
              {formatDateSubtext(props.shippedAt)}
            </MultiTimezoneTooltip>
          ) : undefined,
      },
      {
        status: 'delivered',
        label: 'Delivered',
        number: 3,
        warningTooltip: hasThirdPartySerialWarningPhase(
          props.thirdPartySerialWarningPhases,
          'delivered'
        )
          ? getThirdPartySerialWarningTooltip('delivered')
          : undefined,
        subtext: props.deliveredAt ? (
          props.onEditDeliveryConfirmation ? (
            <PhaseDateWithDialogEdit
              date={props.deliveredAt}
              editLabel="Edit delivery confirmation"
              onEdit={props.onEditDeliveryConfirmation}
            />
          ) : (
            <MultiTimezoneTooltip
              timestamp={props.deliveredAt}
              placement="bottom"
            >
              {formatDateSubtext(props.deliveredAt)}
            </MultiTimezoneTooltip>
          )
        ) : undefined,
      },
    ];

    if (props.shipmentType === 'inbound') {
      basePhases.push({
        status: 'received',
        label: 'Received',
        number: 4,
        subtext: props.receivedAt ? (
          props.onEditReceiveConfirmation ? (
            <PhaseDateWithDialogEdit
              date={props.receivedAt}
              editLabel="Edit receive references"
              onEdit={props.onEditReceiveConfirmation}
            />
          ) : (
            <MultiTimezoneTooltip
              timestamp={props.receivedAt}
              placement="bottom"
            >
              {formatDateSubtext(props.receivedAt)}
            </MultiTimezoneTooltip>
          )
        ) : undefined,
      });
    }

    return basePhases;
  }, [
    props.shipmentType,
    props.createdAt,
    props.shippedAt,
    props.deliveredAt,
    props.receivedAt,
    props.thirdPartySerialWarningPhases,
    props.onEditShippedDate,
    props.onEditShipConfirmation,
    props.onEditDeliveryConfirmation,
    props.onEditReceiveConfirmation,
  ]);

  const terminalStatuses =
    props.shipmentType === 'outbound'
      ? OUTBOUND_TERMINAL_STATUSES
      : INBOUND_TERMINAL_STATUSES;

  return (
    <PhaseIndicator
      phases={phases}
      currentStatus={props.currentStatus}
      cancelledStatus="cancelled"
      terminalStatuses={terminalStatuses}
      cancelledReason={props.cancelledReason}
    />
  );
}
